import { Button } from '@chakra-ui/react'
import type { ReactNode } from 'react'

import type { TranslateFn } from '../types'
import { createTranslator } from '../utils/createTranslator'
import {
  AlertState,
  CenteredState,
  type StatusStateLayout,
} from './statusState'

export type ErrorStateProps = {
  /** Visual layout. Default is the web-app centered look. */
  layout?: StatusStateLayout
  /** Optional icon. Alert layout uses the Chakra error indicator when omitted. */
  icon?: ReactNode
  /** Heading. Defaults to "Error". */
  title?: string
  /** Supporting error detail. */
  description?: string
  /** Custom action node. Takes precedence over `onRetry`. */
  action?: ReactNode
  /** Renders a "Try again" button when `action` is not provided. */
  onRetry?: () => void
  /** Label for the retry button. Defaults to "Try again". */
  retryLabel?: string
  /** Optional translation function for default title and retry label. */
  t?: TranslateFn
}

/**
 * Error display with optional retry action. Use `layout="alert"` for the
 * inline alert used by list pages.
 */
export const ErrorState = ({
  layout = 'centered',
  icon,
  title,
  description,
  action,
  onRetry,
  retryLabel,
  t,
}: ErrorStateProps) => {
  const tr = createTranslator(t)
  const resolvedTitle =
    title === undefined ? tr('errorState.title', 'Error') : title
  const resolvedRetryLabel =
    retryLabel === undefined ? tr('errorState.retry', 'Try again') : retryLabel
  const resolvedAction =
    action ??
    (onRetry ? (
      <Button variant="outline" size="sm" onClick={onRetry}>
        {resolvedRetryLabel}
      </Button>
    ) : undefined)

  if (layout === 'alert') {
    return (
      <AlertState
        status="error"
        icon={icon}
        title={resolvedTitle}
        description={description}
        action={resolvedAction}
      />
    )
  }

  return (
    <CenteredState
      icon={icon}
      title={resolvedTitle}
      description={description}
      action={resolvedAction}
      titleColor="fg.error"
      role="alert"
    />
  )
}
